import React from 'react';
import './Typography.module.css';

/* 
 * Typography Component - Figma-exact implementation
 * Maps directly to Figma design system specifications
 * Uses design tokens from /src/styles/design-tokens.css
 */

export interface TypographyProps {
  children: React.ReactNode;
  variant: 'cta-label' | 'body-copy' | 'desktop-body-copy' | 'h4';
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div' | 'label' | 'time';
  color?: 'default' | 'heading' | 'nav-primary' | 'nav-primary-invert' | 'nav-tertiary-faded' | 'nav-tertiary-invert-faded' |
          'atacama' | 'iguazu' | 'mendoza' | 'santa-catarina' | 'patagonia' | 'awasi' | 'off-white';
  className?: string;
  id?: string;
}

// Helper function to get default HTML element for each variant
const getDefaultElement = (variant: TypographyProps['variant']): TypographyProps['as'] => {
  switch (variant) {
    case 'h4':
      return 'h2'; // Default to h2 for semantic structure
    case 'cta-label':
      return 'span';
    case 'body-copy':
    case 'desktop-body-copy':
      return 'p';
    default:
      return 'span';
  }
};

// Helper function to combine class names
const cn = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

/**
 * Universal Typography Component
 * Implements exact Figma typography specifications
 */
export const Typography: React.FC<TypographyProps> = ({
  children,
  variant,
  as,
  color = 'default',
  className,
  id,
  ...props
}) => {
  const Component = (as || getDefaultElement(variant)) as React.ElementType;
  
  const variantClass = `typography--${variant}`;
  const colorClass = color !== 'default' ? `typography--${color}` : '';

  return (
    <Component
      id={id}
      className={cn(
        'typography', // Base typography class
        variantClass,  // Figma-exact variant class
        colorClass,    // Color variant class
        className      // Additional custom classes
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

/**
 * Semantic Typography Components
 * Pre-configured variants for common use cases
 */

// CTA Labels - Montserrat SemiBold 11px
export const Label: React.FC<Omit<TypographyProps, 'variant'> & { htmlFor?: string }> = ({ 
  as = 'label', 
  ...props 
}) => (
  <Typography variant="cta-label" as={as} {...props} />
);

// Body Text - Montserrat Medium 14px
export const Body: React.FC<Omit<TypographyProps, 'variant'> & { 
  desktop?: boolean 
}> = ({ 
  desktop = false, 
  as = 'p', 
  ...props 
}) => (
  <Typography 
    variant={desktop ? 'desktop-body-copy' : 'body-copy'} 
    as={as} 
    {...props} 
  />
);

// Headings - Kepler Std Display 30px
export const Heading: React.FC<Omit<TypographyProps, 'variant'> & { 
  level: 1 | 2 | 3 | 4 | 5 | 6 
}> = ({ 
  level, 
  as, 
  color = 'heading', 
  ...props 
}) => (
  <Typography 
    variant="h4" 
    as={as || (`h${level}` as TypographyProps['as'])} 
    color={color}
    {...props} 
  />
);

// Navigation Text - CTA Label styling for nav elements
export const NavText: React.FC<Omit<TypographyProps, 'variant'> & { 
  variant?: 'primary' | 'primary-invert' | 'tertiary-faded' | 'tertiary-invert-faded'
}> = ({ 
  variant: navVariant = 'primary', 
  as = 'span', 
  ...props 
}) => (
  <Typography 
    variant="cta-label" 
    as={as} 
    color={`nav-${navVariant}` as TypographyProps['color']}
    {...props} 
  />
);

// Button Text - CTA Label styling for buttons
export const ButtonText: React.FC<Omit<TypographyProps, 'variant'>> = ({ 
  as = 'span', 
  ...props 
}) => (
  <Typography variant="cta-label" as={as} {...props} />
);

// Export the main Typography component as default
export default Typography;